const OMIT_VALUE = Symbol('omit-value');

const cloneValue = (value) => {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
};

const normalizeSelection = (selection) => {
  if (selection === null || selection === undefined || selection === '') {
    return [];
  }

  return Array.isArray(selection) ? selection : [selection];
};

const isFilled = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return value !== null && value !== undefined && value !== '';
};

export const isGltfRenderConditionMatched = (condition, parameters) => {
  if (!condition) return true;

  if (typeof condition === 'string') {
    return isFilled(parameters[condition]);
  }

  if (Array.isArray(condition.all)) {
    return condition.all.every((child) => isGltfRenderConditionMatched(child, parameters));
  }

  if (Array.isArray(condition.any)) {
    return condition.any.some((child) => isGltfRenderConditionMatched(child, parameters));
  }

  if (condition.not) {
    return !isGltfRenderConditionMatched(condition.not, parameters);
  }

  const parameterName = condition.param || condition.field;
  if (!parameterName) return false;

  const parameterValue = parameters[parameterName];

  if (Object.prototype.hasOwnProperty.call(condition, 'includes')) {
    return normalizeSelection(parameterValue).includes(condition.includes);
  }

  if (Object.prototype.hasOwnProperty.call(condition, 'equals')) {
    return parameterValue === condition.equals;
  }

  if (Object.prototype.hasOwnProperty.call(condition, 'notEquals')) {
    return parameterValue !== condition.notEquals;
  }

  if (condition.notEmpty) {
    return isFilled(parameterValue);
  }

  return isFilled(parameterValue);
};

const getValueByPath = (source, path) => {
  if (!source || typeof path !== 'string') return undefined;

  const unsafeSegments = ['__proto__', 'prototype', 'constructor'];
  const segments = path.split('.');
  if (segments.some((segment) => !segment || unsafeSegments.includes(segment))) {
    return undefined;
  }

  return segments.reduce((value, segment) => {
    if (!value || !Object.prototype.hasOwnProperty.call(value, segment)) {
      return undefined;
    }
    return value[segment];
  }, source);
};

const hasOwnProperty = (source, key) => Object.prototype.hasOwnProperty.call(source, key);

const resolveRuntimeDefaultSource = (definition, sources) => {
  if (!definition) return undefined;

  if (Array.isArray(definition)) {
    return definition
      .map((item) => resolveRuntimeDefaultSource(item, sources))
      .find((value) => value !== null && value !== undefined);
  }

  if (Array.isArray(definition.sources)) {
    return resolveRuntimeDefaultSource(definition.sources, sources);
  }

  let value = hasOwnProperty(definition, 'defaultValue') || hasOwnProperty(definition, 'value')
    ? cloneValue(definition.defaultValue ?? definition.value)
    : getValueByPath(sources[definition.source], definition.path);

  if (definition.lookup) {
    const lookupValue = resolveRuntimeDefaultSource(definition.lookup.valueSource, sources);
    const matchedItem = Array.isArray(value)
      ? value.find((item) => getValueByPath(item, definition.lookup.itemPath) === lookupValue)
      : undefined;
    value = getValueByPath(matchedItem, definition.lookup.resultPath);
  }

  if ((value === null || value === undefined) && definition.fallback) {
    return resolveRuntimeDefaultSource(definition.fallback, sources);
  }

  return value;
};

export const resolveFieldRuntimeDefaults = (fields, sources) => Object.fromEntries(
  fields
    .map((field) => {
      if (!field.defaultSource) return null;

      const value = resolveRuntimeDefaultSource(field.defaultSource, sources);

      return value === null || value === undefined
        ? null
        : [field.id, cloneValue(value)];
    })
    .filter(Boolean),
);

export const enforceExclusiveCatalogGroups = (previousValues, values, catalog) => {
  const addedValues = values.filter((value) => !previousValues.includes(value));

  return addedValues.reduce((selectedValues, addedValue) => {
    const exclusiveGroup = catalog[addedValue]?.exclusiveGroup;
    if (!exclusiveGroup) return selectedValues;

    return selectedValues.filter((value) => value === addedValue
      || catalog[value]?.exclusiveGroup !== exclusiveGroup);
  }, values);
};

const mergeExtractedValues = (target, source) => {
  Object.entries(source).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      const existingValues = Array.isArray(target[key]) ? target[key] : [];
      target[key] = [...new Set([...existingValues, ...value])];
      return;
    }

    if (value !== undefined) {
      target[key] = cloneValue(value);
    }
  });
};

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const matchAndExtractTemplate = (template, value, catalogs) => {
  if (Array.isArray(template)) {
    if (!Array.isArray(value)) return { matched: false, values: {} };

    if (template.length === 1 && isObject(template[0]) && hasOwnProperty(template[0], '$param')) {
      return {
        matched: true,
        values: {
          [template[0].$param]: value[0],
        },
      };
    }

    if (template.length > value.length) return { matched: false, values: {} };

    return template.reduce((result, item, index) => {
      if (!result.matched) return result;

      const itemResult = matchAndExtractTemplate(item, value[index], catalogs);
      if (!itemResult.matched) return { matched: false, values: {} };

      mergeExtractedValues(result.values, itemResult.values);
      return result;
    }, { matched: true, values: {} });
  }

  if (!isObject(template)) {
    return {
      matched: template === value,
      values: {},
    };
  }

  if (hasOwnProperty(template, '$param')) {
    return {
      matched: value !== undefined,
      values: {
        [template.$param]: cloneValue(value),
      },
    };
  }

  if (hasOwnProperty(template, '$if')) {
    if (value === undefined) return { matched: true, values: {} };
    return matchAndExtractTemplate(template.$value, value, catalogs);
  }

  if (hasOwnProperty(template, '$selected')) {
    if (!Array.isArray(value)) return { matched: false, values: {} };

    const directive = template.$selected;
    const catalog = catalogs[directive.catalog] || {};
    const templateKey = directive.templateKey || 'template';
    const selectedKeys = [];
    const extractedValues = {};

    const allItemsMatched = value.every((item) => {
      const matchedEntry = Object.entries(catalog).find(([, catalogItem]) => {
        const itemTemplate = catalogItem?.[templateKey];
        if (!itemTemplate) return false;

        const itemResult = matchAndExtractTemplate(itemTemplate, item, catalogs);
        return itemResult.matched;
      });

      if (!matchedEntry) return false;

      const [key, catalogItem] = matchedEntry;
      const itemResult = matchAndExtractTemplate(catalogItem[templateKey], item, catalogs);
      selectedKeys.push(key);
      mergeExtractedValues(extractedValues, itemResult.values);
      return true;
    });

    if (!allItemsMatched) return { matched: false, values: {} };

    return {
      matched: true,
      values: {
        ...extractedValues,
        [directive.param]: selectedKeys,
      },
    };
  }

  if (!isObject(value)) return { matched: false, values: {} };

  return Object.entries(template).reduce((result, [key, childTemplate]) => {
    if (!result.matched) return result;

    if (!hasOwnProperty(value, key)) {
      if (isObject(childTemplate) && hasOwnProperty(childTemplate, '$if')) {
        return result;
      }

      return { matched: false, values: {} };
    }

    const childResult = matchAndExtractTemplate(childTemplate, value[key], catalogs);
    if (!childResult.matched) return { matched: false, values: {} };

    mergeExtractedValues(result.values, childResult.values);
    return result;
  }, { matched: true, values: {} });
};

export const extractGltfRenderTemplateParameters = (template, value, catalogs) => {
  const result = matchAndExtractTemplate(template, value, catalogs);
  return result.matched ? result.values : {};
};

export const resolveGltfRenderTemplate = (value, parameters, catalogs) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => resolveGltfRenderTemplate(item, parameters, catalogs))
      .filter((item) => item !== OMIT_VALUE);
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  if (Object.prototype.hasOwnProperty.call(value, '$param')) {
    const parameterValue = parameters[value.$param];
    return parameterValue === null || parameterValue === undefined
      ? OMIT_VALUE
      : cloneValue(parameterValue);
  }

  if (Object.prototype.hasOwnProperty.call(value, '$if')) {
    return isGltfRenderConditionMatched(value.$if, parameters)
      ? resolveGltfRenderTemplate(value.$value, parameters, catalogs)
      : OMIT_VALUE;
  }

  if (Object.prototype.hasOwnProperty.call(value, '$selected')) {
    const directive = value.$selected;
    const catalog = catalogs[directive.catalog] || {};
    const templateKey = directive.templateKey || 'template';

    return normalizeSelection(parameters[directive.param])
      .map((key) => catalog[key]?.[templateKey])
      .filter(Boolean)
      .map((template) => resolveGltfRenderTemplate(template, parameters, catalogs))
      .filter((selectedValue) => selectedValue !== OMIT_VALUE);
  }

  if (Object.prototype.hasOwnProperty.call(value, '$concat')) {
    return value.$concat.flatMap((item) => {
      const resolvedValue = resolveGltfRenderTemplate(item, parameters, catalogs);
      if (resolvedValue === OMIT_VALUE) return [];
      return Array.isArray(resolvedValue) ? resolvedValue : [resolvedValue];
    });
  }

  return Object.fromEntries(
    Object.entries(value)
      .map(([key, child]) => [key, resolveGltfRenderTemplate(child, parameters, catalogs)])
      .filter(([, child]) => child !== OMIT_VALUE),
  );
};
